import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaProjectDiagram, FaUser, FaCalendar, FaClock, FaEye, FaEdit, FaUsers } from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface Project {
  _id: string;
  projectName: string;
  industry: string;
  description: string;
  userId: string;
  status: string;
  platformInfo: Array<{
    platform_name: string;
    platform_type: string;
    user_roles: string[];
    technical_details: string;
  }>;
  userPersona: Array<{
    name: string;
    demographics: string[];
    behaviors: string[];
    goals: string[];
    pain_points: string[];
  }>;
  valueProps: Array<{
    persona: string;
    value_proposition: string;
  }>;
  features: Array<{
    name: string;
    priority: string;
    technical_details: string;
    non_technical_details: string;
  }>;
  requirements: Array<{
    requirement_name: string;
    functional_requirements: string[];
    non_functional_requirements: string[];
    technical_requirements: string[];
    complexity_assessment: {
      overall_complexity: string;
      reasoning: string;
      risk_factors: string[];
    };
    third_party_services: string[];
  }>;
  currentDocumentVersion: number;
  document: {
    prd?: {
      markdown: string;
      word: string;
      pdf: string;
    };
    userStory?: {
      markdown: string;
      word: string;
      pdf: string;
    };
    testCase?: {
      markdown: string;
      word: string;
      pdf: string;
    };
    businessPlan?: {
      markdown: string;
      word: string;
      pdf: string;
    };
    investorDeck?: {
      word: string;
      pdf: string;
      ppt: string;
    };
  };
  projectStatus: string;
  isDeleted: boolean;
  coreBusinessQuestion: Array<{
    questionId: string;
    answer: string;
    answerArr: string[];
    _id: string;
  }>;
  followUpQuestion: Array<{
    question: string;
    suggestedAnswer: string;
    answer: string;
    _id: string;
  }>;
  lastDocumentUpdated: string;
  competitors: any[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [projectOwner, setProjectOwner] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      setIsLoading(true);
      
      // Fetch complete project data by ID with joined user data
      console.log(`Fetching project details for ID: ${projectId}`);
      
      // Try to fetch project with user data included
      const response = await api.get(`/admin/projects/${projectId}?include=user`);
      const projectData = response.data.data || response.data;
      
      // Log the fetched data for debugging
      console.log('Project data fetched:', projectData);
      
      // Set the complete project data
      setProject(projectData);
      
      // Check if owner data was included in the project response
      if (projectData && projectData.user) {
        setProjectOwner(projectData.user);
        console.log('Project owner data found in project response:', projectData.user);
      } else if (projectData && projectData.userId) {
        // If owner data wasn't included, try to fetch it separately
        try {
          console.log(`Fetching project owner details for user ID: ${projectData.userId}`);
          const ownerResponse = await api.get(`/admin/users/${projectData.userId}`);
          const ownerData = ownerResponse.data.data || ownerResponse.data;
          setProjectOwner(ownerData);
          console.log('Project owner data fetched separately:', ownerData);
        } catch (ownerError) {
          console.log('Could not fetch project owner details:', ownerError);
          setProjectOwner(null);
        }
      }
      
    } catch (error) {
      console.error('Error fetching project details:', error);
      // Set null project on API failure - will show error state
      setProject(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      completed: { color: 'bg-blue-100 text-blue-800', label: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Project not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FaProjectDiagram className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Project Details</h1>
              <p className="text-gray-600 mt-1">View comprehensive project information</p>
            </div>
          </div>
        </div>

        {/* Project Overview Card */}
        <div className="bg-gradient-to-r from-primary to-primaryDark rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaProjectDiagram className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{project.projectName}</h2>
                <p className="text-primaryLight">{project.industry}</p>
                <div className="flex items-center space-x-4 mt-2">
                  {getStatusBadge(project.status)}
                  <span className="text-sm">
                    Version {project.currentDocumentVersion}
                  </span>
                </div>
              </div>
            </div>
            {projectOwner && (
              <div className="text-right">
                <p className="text-sm text-primaryLight">Project Owner</p>
                <p className="font-medium">{projectOwner.name}</p>
                <p className="text-sm text-primaryLight">{projectOwner.email}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Project Description */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <FaEdit className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-semibold text-gray-900">Project Description</h3>
        </div>
        <div className="prose max-w-none">
          <p className="text-gray-700 leading-relaxed">
            {project.description || 'No description available for this project.'}
          </p>
        </div>
      </motion.div>

      {/* Project Owner Details */}
      {projectOwner && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaUser className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Project Owner Details</h3>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-lg font-medium">
                {projectOwner.name ? projectOwner.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">{projectOwner.name}</h4>
                <p className="text-gray-500">{projectOwner.email}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    projectOwner.role === 'agency_admin' ? 'bg-blue-100 text-blue-800' :
                    projectOwner.role === 'agency_member' ? 'bg-green-100 text-green-800' :
                    projectOwner.role === 'member' ? 'bg-gray-100 text-gray-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {projectOwner.role === 'agency_admin' ? 'Agency Admin' :
                     projectOwner.role === 'agency_member' ? 'Agency Member' :
                     projectOwner.role === 'member' ? 'Member' :
                     projectOwner.role || 'Unknown'}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    projectOwner.disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {projectOwner.disabled ? 'Disabled' : 'Active'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Member since: {formatDate(projectOwner.createdAt)}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/users/${projectOwner._id}`)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors"
            >
              View Profile
            </button>
          </div>
        </motion.div>
      )}

      {/* Project Information Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Project Information</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Project Name</label>
              <p className="text-gray-900 font-medium">{project.projectName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Industry</label>
              <p className="text-gray-900 font-medium">{project.industry}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">{getStatusBadge(project.status)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Project Status</label>
              <p className="text-gray-900 font-medium capitalize">{project.projectStatus?.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Document Version</label>
              <p className="text-gray-900 font-medium">v{project.currentDocumentVersion}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Project ID</label>
              <p className="text-gray-900 font-medium font-mono text-xs">{project._id}</p>
            </div>
          </div>
        </motion.div>

        {/* Timeline Information */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaCalendar className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Timeline</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Created On</label>
              <p className="text-gray-900 font-medium">{formatDate(project.createdAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900 font-medium">{formatDate(project.updatedAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Document Update</label>
              <p className="text-gray-900 font-medium">{formatDate(project.lastDocumentUpdated)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.isDeleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {project.isDeleted ? 'Deleted' : 'Active'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Platform Information */}
      {project.platformInfo && project.platformInfo.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Platform Information</h3>
          </div>
          <div className="space-y-6">
            {project.platformInfo.map((platform, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Platform Name</label>
                    <p className="text-gray-900 font-medium">{platform.platform_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Platform Type</label>
                    <p className="text-gray-900 font-medium">{platform.platform_type}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-500">User Roles</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {platform.user_roles.map((role, roleIndex) => (
                      <span key={roleIndex} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Technical Details</label>
                  <p className="text-gray-700 text-sm leading-relaxed mt-1">{platform.technical_details}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Features */}
      {project.features && project.features.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Features</h3>
          </div>
          <div className="space-y-4">
            {project.features.map((feature, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{feature.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    feature.priority === 'High' ? 'bg-red-100 text-red-800' :
                    feature.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {feature.priority} Priority
                  </span>
                </div>
                {feature.technical_details && (
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-500">Technical Details</label>
                    <p className="text-gray-700 text-sm">{feature.technical_details}</p>
                  </div>
                )}
                {feature.non_technical_details && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Non-Technical Details</label>
                    <p className="text-gray-700 text-sm">{feature.non_technical_details}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* User Personas */}
      {project.userPersona && project.userPersona.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaUser className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">User Personas</h3>
          </div>
          <div className="space-y-6">
            {project.userPersona.map((persona, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">{persona.name}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Demographics</label>
                    <ul className="text-sm text-gray-700 mt-1 space-y-1">
                      {persona.demographics.map((demo, demoIndex) => (
                        <li key={demoIndex} className="flex items-start">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {demo}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Behaviors</label>
                    <ul className="text-sm text-gray-700 mt-1 space-y-1">
                      {persona.behaviors.map((behavior, behaviorIndex) => (
                        <li key={behaviorIndex} className="flex items-start">
                          <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {behavior}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Goals</label>
                    <ul className="text-sm text-gray-700 mt-1 space-y-1">
                      {persona.goals.map((goal, goalIndex) => (
                        <li key={goalIndex} className="flex items-start">
                          <span className="w-1 h-1 bg-green-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {goal}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Pain Points</label>
                    <ul className="text-sm text-gray-700 mt-1 space-y-1">
                      {persona.pain_points.map((painPoint, painIndex) => (
                        <li key={painIndex} className="flex items-start">
                          <span className="w-1 h-1 bg-red-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {painPoint}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Generated Documents */}
      {project.document && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Generated Documents</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {project.document.prd && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">PRD (Product Requirements Document)</h4>
                <div className="space-y-2">
                  <a href={project.document.prd.pdf} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PDF Version</a>
                  <a href={project.document.prd.word} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">Word Version</a>
                </div>
              </div>
            )}
            {project.document.userStory && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">User Stories</h4>
                <div className="space-y-2">
                  <a href={project.document.userStory.pdf} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PDF Version</a>
                  <a href={project.document.userStory.word} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">Word Version</a>
                </div>
              </div>
            )}
            {project.document.testCase && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Test Cases</h4>
                <div className="space-y-2">
                  <a href={project.document.testCase.pdf} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PDF Version</a>
                  <a href={project.document.testCase.word} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">Word Version</a>
                </div>
              </div>
            )}
            {project.document.businessPlan && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Business Plan</h4>
                <div className="space-y-2">
                  <a href={project.document.businessPlan.pdf} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PDF Version</a>
                  <a href={project.document.businessPlan.word} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">Word Version</a>
                </div>
              </div>
            )}
            {project.document.investorDeck && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Investor Deck</h4>
                <div className="space-y-2">
                  <a href={project.document.investorDeck.pdf} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PDF Version</a>
                  <a href={project.document.investorDeck.word} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">Word Version</a>
                  <a href={project.document.investorDeck.ppt} target="_blank" rel="noopener noreferrer" 
                     className="block text-sm text-blue-600 hover:text-blue-800">PowerPoint Version</a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Core Business Questions */}
      {project.coreBusinessQuestion && project.coreBusinessQuestion.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Core Business Questions</h3>
          </div>
          <div className="space-y-4">
            {project.coreBusinessQuestion.map((question, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-500">Answer</label>
                  <p className="text-gray-900 font-medium">{question.answer}</p>
                </div>
                {question.answerArr && question.answerArr.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Answer Options</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {question.answerArr.map((ans, ansIndex) => (
                        <span key={ansIndex} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          {ans}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Follow-up Questions */}
      {project.followUpQuestion && project.followUpQuestion.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <FaProjectDiagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">Follow-up Questions</h3>
          </div>
          <div className="space-y-4">
            {project.followUpQuestion.map((question, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-500">Question</label>
                  <p className="text-gray-900 font-medium">{question.question}</p>
                </div>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-500">Suggested Answer</label>
                  <p className="text-gray-700 text-sm">{question.suggestedAnswer}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Actual Answer</label>
                  <p className="text-gray-900 font-medium">{question.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default ProjectDetail; 